import {
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    HostListener,
    OnInit,
    Input,
    Output,
    ViewChild,
    SimpleChanges,
    OnChanges,
    ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ActionStatus,
    JobAction,
    JobActionType,
    JobActionUpdate,
} from '@intranet/modules/actions';
import { getStatusByValue, shouldHighlightDate } from '../../action.utils';
import { UserService } from '@intranet/modules/users';
import { PageEvent } from '@angular/material/paginator';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { take } from 'rxjs/operators';
import { ActionService } from '@intranet/modules/actions';
import { TogglService } from '@intranet/modules/toggl';
import { Subscription, forkJoin } from 'rxjs';
import { JobService } from '@intranet/modules/jobs/job.service';
import { Job } from '@intranet/modules/jobs/job.types';
import { DateAdapter } from '@angular/material/core';

@Component({
    selector: 'action-table',
    templateUrl: './action-table.component.html',
    styleUrls: ['./action-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed',  style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('expanded',   style({ height: '*', visibility: 'visible' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ])
    ]
})
export class ActionTableComponent implements OnInit, DoCheck, OnChanges {
    @ViewChild('schedulepicker', { static: false }) schedulepicker;
    @ViewChild('deadlinepicker', { static: false }) deadlinepicker;
    @Input() header: boolean = true;
    @Input() title: string;
    @Input() sortBy: string;
    @Input() sortDir: string;
    @Input() hideClient: boolean = false;
    @Input() hideJob: boolean = false;
    @Input() disableSort: boolean = false;
    @Input() actions: JobAction[];
    @Input() tickedActions: JobAction[];
    @Input() loggedInUserId: string;
    @Input() isAdmin = false;
    @Input() hidePagination = false;
    @Input() pageIndex = 0;
    @Input() totalResults = 0;
    @Output() update = new EventEmitter<JobAction>();
    @Output() delete = new EventEmitter<JobAction>();
    @Output() paginate = new EventEmitter<PageEvent>();
    @Output() sort = new EventEmitter<string>();
    @Output() selected: EventEmitter<JobActionUpdate> = new EventEmitter();
    @Output() bulkAction: EventEmitter<{actions: JobAction[], keepTicked: (ids:number[]) => void }> = new EventEmitter();
    @Input() isPreview: boolean = false;
    @Input() copyMode: boolean = false;
    @Output() selectionChanged = new EventEmitter<JobAction[]>();
    @Input() marginData: { [jobId: number]: any };
    @Input() hideMarginBar = false;
    @Input() hideTogglButton = false;
    @Input() showActions: boolean = true;
    @Input() matchTimerByDescription = true;
    // The Actions page's planner extras: the Planned column and "Add to
    // planner" in each row's ⋮ menu. Off everywhere else this table is
    // used (Job page, Home, job copy…), which stay exactly as they were.
    @Input() plannerFeatures = false;
    @Output() addToPlanner = new EventEmitter<JobAction>();
    // "Planned blocks" in the ⋮ menu, or the Planned flag clicked — the
    // page opens the list of the action's blocks.
    @Output() viewPlannedBlocks = new EventEmitter<JobAction>();

    editingIndex: number = -1;
    highlightedActionId: number = null;
    showMoreActions = false;

    selectedActions: JobAction[] = [];
    openAvatarRowIndex: number | null = null;
    private assignedUsersMap = new Map<number, {id:string;name:string;imageUrl?:string}[]>();
    private assignedUserNamesMap = new Map<number, string[]>();
    private assignedTeamNamesMap = new Map<number, string[]>();
    expandedActionIds = new Set<number>();
    relatedActions: { [jobId: number]: JobAction[] } = {};
    jobActionsMap: { [jobId: number]: JobAction[] } = {};
    jobActionCounts: { [jobId: number]: number } = {};
    runningJobId: number | null = null;
    private _togglSub = new Subscription();
    archivedJobIds = new Set<number>();

    isExpandedAction(actionId: number): boolean {
        return this.expandedActionIds.has(actionId);
    }

    public isFirstActionOfJob(action: JobAction, idx: number): boolean {
        return this.actions.findIndex(a => a.jobId === action.jobId) === idx;
    }

    constructor(
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        protected userService: UserService,
        private eRef: ElementRef,
        private _actionService: ActionService,
        private cd: ChangeDetectorRef,
        private togglService: TogglService,
        private jobService: JobService,
        private dateAdapter: DateAdapter<any>
    ) {
        this.highlightedActionId =
            +this._activatedRoute.snapshot.queryParamMap.get('highlight');
    }

    ngOnInit() {
        this._togglSub.add(
            this.togglService.togglRunningJob$
                .subscribe(r => {
                    this.runningJobId = r ? r.jobId : null;
                    this.cd.detectChanges();
                })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.actions && this.actions) {
            this.assignedUsersMap.clear();
            this.assignedUserNamesMap.clear();
            this.assignedTeamNamesMap.clear();

            this.jobActionsMap = {};
            this.actions.forEach(a => {
                (this.jobActionsMap[a.jobId] ||= []).push(a);
            });

            // for each distinct job, get the count and—if >1—fetch the full list
            const uniqueJobIds = Array.from(new Set(this.actions.map(a => a.jobId)));
            uniqueJobIds.forEach(jobId => {
                this._actionService
                    .getActionCount(jobId)
                    .pipe(take(1))
                    .subscribe(count => {
                        this.jobActionCounts[jobId] = count;
                        this.cd.detectChanges();

                        if (count > 1) {
                            this._actionService
                                .fetchAllForJob(jobId)
                                .pipe(take(1))
                                .subscribe(fullList => {
                                    this.relatedActions[jobId] = fullList;
                                    this.cd.detectChanges();
                                });
                        }
                  });
              });
        }

        if (changes.actions && this.actions?.length) {
          const uniqueJobIds = Array.from(new Set(this.actions.map(a => a.jobId)));
      
          // turn each id into a getJob(id) Observable<Job>
          const calls = uniqueJobIds.map(id =>
            this.jobService.getJob(id).pipe(take(1))
          );
      
          // wait for _all_ of them to come back
          forkJoin(calls).subscribe((jobs: Job[]) => {
            // collect the archived ones
            this.archivedJobIds = new Set(
              jobs.filter(j => j.archive).map(j => j.jobId)
            );
            this.cd.markForCheck();
          });
        }
    }

    ngOnDestroy() {
        this._togglSub.unsubscribe();
    }

    ngDoCheck() {
        // things have changed so we need to update the selectedActions array
        let checkboxes = document.querySelectorAll('.bulkcheck:checked');

        // get an array of the values of the checked checkboxes
        let ids = Array.from(checkboxes).map((checkbox) => +checkbox.getAttribute('value'));
        this.selectedActions = this.actions.filter(a => ids.includes(a.jobActionId));
    }

    get enums() {
        return { JobActionType, ActionStatus };
    }

    mapName(action: JobAction) {
        return (
            this.userService.getUserAndGroupNamesFromIds(
                action.assignedUsers,
                action.assignedGroups
            ) ?? 'Un-assigned'
        );
    }

    mapUserNames(action: JobAction): string[] {
        const names = this.userService.getUserAndGroupNamesFromIds(
            action.assignedUsers,
            action.assignedGroups
        );
        return names ? names.split(',').map(name => name.trim()) : [];
    }

    toggleJobActions(jobId: number, actionId: number, e: MouseEvent) {
        e.stopPropagation();
        const wasOpen = this.expandedActionIds.has(actionId);

        // just collapse everything
        this.expandedActionIds.clear();

        if (!wasOpen && this.relatedActions[jobId]?.length > 1) {
          // expand the clicked action, the data is already in relatedActions[jobId]
          this.expandedActionIds.add(actionId);
        }
    }

    getFullColspan(): number {
      return 3 /* status-icon, priority, avatar */
           + (this.hideClient ? 0 : 1)
           + (this.hideJob    ? 0 : 2)
           + 6 /* title, schedule, deadline, status, jira, notes */
           + (this.plannerFeatures ? 1 : 0) /* planned flag */
           + ((this.isPreview || this.copyMode || this.hideMarginBar) ? 0 : 1)
           + 3 /* preview-toggle + toggl button + actions-menu */;
    }

    // Only live actions go on the planner (the same ones its own "Add
    // planned block" picker lists) — not completed or archived ones.
    canAddToPlanner(action: JobAction): boolean {
        return action.actionStatus !== ActionStatus.Completed
            && action.actionStatus !== ActionStatus.Archived;
    }

    // The ⋮ menu is there for an action that can be planned, or that's on
    // someone's planner (to see its blocks — e.g. a completed one).
    hasPlannerMenu(action: JobAction): boolean {
        return this.plannerFeatures && (this.canAddToPlanner(action) || !!action.hasPlannedBlock);
    }

    onAvatarDropdown(action: JobAction, isOpening: boolean) {
        const rowIndex = this.actions.findIndex(a => a.jobActionId === action.jobActionId);

        if (isOpening) {
            this.openAvatarRowIndex = rowIndex;
        } else if (this.openAvatarRowIndex === rowIndex) {
            this.openAvatarRowIndex = null;
        }
    }

    getAssignedUsersCached(action: JobAction) {
        const key = action.jobActionId;
        if (!this.assignedUsersMap.has(key)) {
            const arr = this.userService
                .getUsersFromIds(action.assignedUsers)
                .map(u => ({
                    id: u.id,
                    name: u.fullName,
                    imageUrl: this.userService.findAvatarForUser(u.id)
                }));
            this.assignedUsersMap.set(key, arr);
        }
        return this.assignedUsersMap.get(key);
    }

    getAssignedTeamNamesCached(action: JobAction): string[] {
        const key = action.jobActionId;
        if (!this.assignedTeamNamesMap.has(key)) {
            // For each assignedGroups ID, look up the group and extract its name.
            const groupNames: string[] = this.userService
                .getGroupsFromIds(action.assignedGroups) // returns UserGroup[]
                .map(g => g.groupName);
            this.assignedTeamNamesMap.set(key, groupNames);
        }
        return this.assignedTeamNamesMap.get(key);
    }

    getAssignedUserNamesCached(action: JobAction): string[] {
        const key = action.jobActionId;

        // If already computed, return the same array instance:
        if (this.assignedUserNamesMap.has(key)) {
            return this.assignedUserNamesMap.get(key);
        }

        // Otherwise, compute once:
        const names: string[] = this.userService
            .getUsersFromIds(action.assignedUsers)
            .map(u => u.fullName ?? `${u.firstName} ${u.surname}`);

        this.assignedUserNamesMap.set(key, names);
        return names;
    }

    getAssignedUsers(action: JobAction): { id: string, name: string, imageUrl?: string }[] {
        return this.userService.getUsersFromIds(action.assignedUsers).map(user => ({
            id: user.id,
            name: user.fullName ?? `${user.firstName} ${user.surname}`,
            imageUrl: this.userService.getAvatarUrl(user.id)
        }));
    }

    getAssignedUserNames(action: JobAction): string[] {
        return this.getAssignedUsers(action).map(u => u.name);
    }

    getUserNames(action: JobAction): string[] {
        return this.userService
            .getUsersFromIds(action.assignedUsers)
            .map(u => u.fullName ?? `${u.firstName} ${u.surname}`);
    }

    getFirstUserId(action: JobAction): string | null {
        return action.assignedUsers?.length ? action.assignedUsers[0] : null;
    }

    getChildActions(jobId: number): JobAction[] {
        return this.relatedActions[jobId] || [];
    }

    getStatus(action: JobAction): string {
        if (action.actionStatus === ActionStatus.Waiting) {
            const name = this.getWaitingUserName(action) ?? this.getWaitingGroupName(action);
            return name ? `Waiting ${name}` : `Waiting`;
        }

        return getStatusByValue(action.actionStatus)?.title ?? '';
    }

    isWaitingForPerson(action: JobAction): boolean {
        return action.actionStatus === ActionStatus.Waiting && !!this.getWaitingUserId(action);
    }

    isWaitingForGroup(action: JobAction): boolean {
        return action.actionStatus === ActionStatus.Waiting
            && !this.getWaitingUserId(action)
            && !!this.getWaitingGroupName(action);
    }

    getWaitingUserId(action: JobAction): string | null {
        return action.waitingUserId ?? null;
    }

    getWaitingUserName(action: JobAction): string | undefined {
        let name = action.waitingUser?.shortName;
        if (!name && action.waitingUserId) {
            const u = this.userService.users.find(u => u.id === action.waitingUserId);
            name = u?.shortName;
        }
        return name;
    }

    getWaitingUserAvatarUrl(action: JobAction): string | undefined {
        const id = this.getWaitingUserId(action);
        return id ? this.userService.findAvatarForUser(id) : undefined;
    }

    getWaitingGroupName(action: JobAction): string | undefined {
        let name = action.waitingGroup?.groupName;
        if (!name && action.waitingGroupId != null) {
            const g = this.userService.groups.find(g => g.userGroupId === action.waitingGroupId);
            name = g?.groupName;
        }
        return name;
    }

    shouldHighlightRow(date: string, warning: boolean = false) {
        return shouldHighlightDate(date, warning);
    }

    isEditingRow(index: number) {
        return index === this.editingIndex;
    }

    @HostListener('document:click', ['$event'])
    cancelEditing(event) {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.editingIndex = -1;
        }
    }

    showMore() {
        return this.actions.length % 50 === 0;
    }

    mostRecentNote(action: JobAction) {
        return action.notes.length > 0 ? action.notes[0].text : '--';
    }

    onPaginate(e: PageEvent) {
        this.paginate.emit(e);
    }

    onEditingRow(index: number) {
        this.editingIndex = index;
    }

    onSelectAction(action: JobAction, propertyKey: string) {
        this.selected.emit(<JobActionUpdate>{ action, propertyKey });
    }

    onDeleteAction(action: JobAction) {
        this.delete.emit(action);
    }

    onActionPropChange({ value }, action: JobAction, prop: string) {
        let updatedAction = <JobAction>{ ...action };
        updatedAction[prop] = value;

        this.editingIndex = -1;
        this.update.emit(updatedAction);
    }

    onSort(prop: string) {
        this.sort.emit(prop);
    }

    onGoToJob({ jobId, jobActionId }: JobAction) {
        this._router.navigateByUrl(
            `/jobs/detail/${jobId}?highlight=${jobActionId}`
        );
    }

    onGoToClient({ clientId }: JobAction) {
        this._router.navigateByUrl(`/clients/dashboard/${clientId}`);
    }

    onToggleMoreActions() {
        this.showMoreActions = !this.showMoreActions;
    }

    onClearDate(action: JobAction, prop: string) {
        this.onActionPropChange({ value: null }, action, prop);
    }

    onStateChange() {
    }

    trackByFn(index, item) {
        return item.jobActionId;
    }

    onBulkChecked(action : JobAction, event: any) {
        if (event.srcElement.checked) {
            this.selectedActions.push(action);
        }
        else {
            this.selectedActions = this.selectedActions.filter(a => a.jobActionId !== action.jobActionId);
        }
    }

    onBulkCheckedAll(event: any) {
        let srcCheckbox = event.srcElement as HTMLInputElement;

        let checkboxes = document.getElementsByClassName('bulkcheck');
        for (let i = 0; i < checkboxes.length; i++) {
            let checkbox = checkboxes[i] as HTMLInputElement;
            checkbox.checked = srcCheckbox.checked;
        }

    }

    onBulkEdit() {
        this.bulkAction.emit({actions: this.selectedActions, keepTicked: this.onKeepTicked.bind(this)});
    }

    openDatepicker(picker: any, event?: MouseEvent, cellDate?: Date | string): void {
        event?.stopPropagation();

        const dp: any = picker?.matDatepickerElement;
        if (!dp) return;

        const today = this.dateAdapter.today();

        // If the cell has a date in the future, open the calendar on that month
        // Otherwise, open on the current month
        if (cellDate) {
            const dateValue = new Date(cellDate);
            if (!isNaN(dateValue.getTime()) && dateValue > today) {
                dp.startAt = dateValue;
            } else {
                dp.startAt = today;
            }
        } else {
            dp.startAt = today;
        }

        dp.open();
    }


    /**
     * Ticks or unticks checkboxes based on the provided ids array.
     * @param ids - An array of numbers representing the ids of checkboxes to be ticked.
     */
    onKeepTicked(ids: number[]) {
        // First clear the Select All checkbox
        let selectAll = document.getElementById('selectAllCheck') as HTMLInputElement;
        selectAll.checked = false;
        // get all checkboxes with the class 'bulk-checkbox'
        let checkboxes = document.getElementsByClassName('bulkcheck');
        // loop through all checkboxes and tick them if their id is in the ids array otherwise untick them
        for (let i = 0; i < checkboxes.length; i++) {
            let checkbox = checkboxes[i] as HTMLInputElement;
            if (ids.includes(+checkbox.value)) {
                checkbox.checked = true;
            }
            else {
                checkbox.checked = false;
            }
        }

        // set this.selectedActions to the actions with ids in the ids array
        this.selectedActions = this.actions.filter(a => ids.includes(a.jobActionId));


    }

    isTicked(action: JobAction) {
        if (!this.tickedActions || this.tickedActions.length === 0) {
            return false;
        }
        return this.tickedActions.some(a => a.jobActionId === action.jobActionId);
    }

    // Handles when a checkbox in copy mode is toggled
    onCopyCheckboxChanged(event: Event, action: JobAction) {
      const checked = (event.target as HTMLInputElement).checked;

      if (checked) {
          if (!this.tickedActions.find(a => a.jobActionId === action.jobActionId)) {
              this.tickedActions.push(action);
          }
      } else {
          this.tickedActions = this.tickedActions.filter(a => a.jobActionId !== action.jobActionId);
      }

      this.selectionChanged.emit([...this.tickedActions]);
    }

    // Handles the "select all" checkbox in copy mode
    onCopySelectAllChanged(event: Event) {
      const checked = (event.target as HTMLInputElement).checked;

      this.tickedActions = checked ? [...this.actions] : [];
      this.selectionChanged.emit([...this.tickedActions]);
    }

    // Determines if all actions are ticked (used for select all in copy mode)
    areAllCopyActionsTicked(): boolean {
      return this.actions?.length > 0 &&
            this.tickedActions?.length === this.actions.length;
    }

    // Determines if *some* actions are ticked (for indeterminate state)
    areSomeCopyActionsTicked(): boolean {
      return this.tickedActions?.length > 0 &&
            this.tickedActions?.length < this.actions.length;
    }

}
